/**
 * Did it work, and what would have made it better?
 *
 * The product's north star is "the share of sessions that end in physical convergence"
 * (`ProductOverview.md` §6), and until now that number came entirely from the proximity check —
 * which measures *two phones being close*, not two people finding each other. Those are usually
 * the same thing and sometimes very much not: a hedge, a fence, a floor, a crowd.
 *
 * So the product asks. One question with two buttons at the moment it matters, and an optional
 * handful more for anybody willing. Both are answered *after* the session is over, which is why
 * this is a separate REST endpoint rather than anything on the socket.
 *
 * **What this deliberately does not carry.** No session code, no participant id, no coordinate,
 * no name, no timestamp finer than the hour. A feedback row cannot be joined to a telemetry row
 * or to a person, and that is not an oversight to be tidied up later — it is the reason the
 * product is allowed to ask at all.
 */

/** The one question that matters, asked with two buttons. */
export type FoundOutcome =
  /** Yes — the arrow got us to each other. */
  | 'found'
  /** No — we gave up, or found each other another way. */
  | 'not_found'
  /** Asked, dismissed. Recorded so the answer rate is knowable. */
  | 'skipped';

/** Where it went wrong, when it did. Closed list — free text is the box below. */
export const FEEDBACK_PROBLEMS = [
  { id: 'accuracy', label: 'The arrow pointed the wrong way' },
  { id: 'last_metres', label: 'Fine until the last few metres' },
  { id: 'obstacle', label: 'Something was in the way' },
  { id: 'signal', label: 'One of us lost signal' },
  { id: 'permissions', label: 'Trouble allowing location or compass' },
  { id: 'confusing', label: 'I could not work out what to do' },
  { id: 'battery', label: 'It ate the battery' },
] as const;

export type FeedbackProblem = (typeof FEEDBACK_PROBLEMS)[number]['id'];

/** Where it was used. Drives the range thresholds far more than any lab measurement will. */
export const FEEDBACK_PLACES = [
  { id: 'beach', label: 'Beach' },
  { id: 'festival', label: 'Festival or event' },
  { id: 'park', label: 'Park or campsite' },
  { id: 'parking', label: 'Car park' },
  { id: 'city', label: 'Streets or old town' },
  { id: 'transport', label: 'Airport or station' },
  { id: 'other', label: 'Somewhere else' },
] as const;

export type FeedbackPlace = (typeof FEEDBACK_PLACES)[number]['id'];

export const MAX_COMMENT_LENGTH = 600;

/** What a device submits. Every field optional but `found`, because a partial answer is an answer. */
export interface FeedbackSubmission {
  found: FoundOutcome;
  /** 1–5, or null if they did not say. Not a star rating — "how well did it work". */
  rating: number | null;
  problems: FeedbackProblem[];
  place: FeedbackPlace | null;
  /** Free text, in the user's own words. The most valuable field and the least structured. */
  comment: string | null;
  /** How many people were in the room. Rounded by the client; a count, not an identity. */
  groupSize: number | null;
  /** Whether this session used a shared meeting point, which is the newest thing to validate. */
  usedRallyPoint: boolean;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const PROBLEM_IDS = new Set<string>(FEEDBACK_PROBLEMS.map((problem) => problem.id));
const PLACE_IDS = new Set<string>(FEEDBACK_PLACES.map((place) => place.id));

/**
 * Validate a submission, rebuilding it field by field.
 *
 * Nothing is passed through. A client cannot smuggle an extra property into the feedback table
 * by adding it to the request body, which is the same rule the telemetry parser follows and for
 * the same reason.
 */
export function parseFeedback(value: unknown): FeedbackSubmission | null {
  if (!isRecord(value)) return null;

  const found = value['found'];
  if (found !== 'found' && found !== 'not_found' && found !== 'skipped') return null;

  const rawRating = value['rating'];
  const rating =
    typeof rawRating === 'number' && Number.isFinite(rawRating)
      ? Math.min(5, Math.max(1, Math.round(rawRating)))
      : null;

  const rawProblems = Array.isArray(value['problems']) ? value['problems'] : [];
  const problems = [
    ...new Set(
      rawProblems.filter(
        (problem): problem is FeedbackProblem =>
          typeof problem === 'string' && PROBLEM_IDS.has(problem),
      ),
    ),
  ];

  const rawPlace = value['place'];
  const place =
    typeof rawPlace === 'string' && PLACE_IDS.has(rawPlace) ? (rawPlace as FeedbackPlace) : null;

  const rawComment = value['comment'];
  const comment =
    typeof rawComment === 'string' && rawComment.trim().length > 0
      ? rawComment.trim().slice(0, MAX_COMMENT_LENGTH)
      : null;

  const rawGroup = value['groupSize'];
  const groupSize =
    typeof rawGroup === 'number' && Number.isFinite(rawGroup)
      ? Math.min(8, Math.max(1, Math.round(rawGroup)))
      : null;

  return {
    found,
    rating,
    problems,
    place,
    comment,
    groupSize,
    usedRallyPoint: value['usedRallyPoint'] === true,
  };
}

/**
 * Does a comment look like it contains coordinates?
 *
 * The free-text box is the one field a user could put a location into, and the product's promise
 * is that no coordinate reaches durable storage. Somebody typing "we were at 37.9838, 23.7275"
 * is not an attacker — but storing it would still break the rule, so it is stripped.
 */
export function stripCoordinates(comment: string): string {
  // A decimal pair, with or without a comma: the shape almost every copy-paste produces.
  return comment.replace(/-?\d{1,3}\.\d{4,}\s*[,;]?\s*-?\d{1,3}\.\d{4,}/g, '[location removed]');
}
